import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Upload, Plus, RefreshCw, HardDrive, Calendar, BarChart3, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { FileUpload } from './FileUpload';
import { StockData } from '../types';
import { 
  getDatabaseList, 
  loadDatabase, 
  deleteDatabase, 
  mergeDataIntoDatabase,
  getDatabaseStorageInfo,
  DatabaseMetadata,
  clearDatabaseCache
} from '../utils/optimizedDatabaseManager';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { ErrorBoundary } from './ErrorBoundary';
import { trackRenderTime, trackComponentLifecycle } from '../utils/performance';
import { useNetworkResilience } from '../hooks/useNetworkResilience';
import { useApiCache } from '../hooks/useApiCache';

interface DatabaseSelectionProps {
  onDatabaseSelected: (data: StockData[], databaseName: string) => void;
  onError: (error: string) => void;
}

export const DatabaseSelection: React.FC<DatabaseSelectionProps> = ({ 
  onDatabaseSelected, 
  onError 
}) => {
  const navigate = useNavigate();
  const [databases, setDatabases] = useState<DatabaseMetadata[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [newDatabaseName, setNewDatabaseName] = useState('');
  const [newDatabaseDescription, setNewDatabaseDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true);

  const { resilientFetch } = useNetworkResilience();
  const { cachedFetch, invalidate: invalidateCache } = useApiCache<DatabaseMetadata[]>();
  const [storageInfo, setStorageInfo] = useState({
    totalDatabases: 0,
    totalRecords: 0,
    totalSizeBytes: 0,
    totalSizeMB: '∞',
    totalLocalStorageUsed: '0.00',
    storageUsagePercentage: '0.0',
    isNearLimit: false
  });
  
  // Track component render time
  const finishRenderTracking = trackRenderTime('DatabaseSelection');
  
  // Track component lifecycle
  useEffect(() => {
    return trackComponentLifecycle('DatabaseSelection');
  }, []);

  useEffect(() => {
    loadDatabaseList();
    loadStorageInfo();
  }, []);

  const loadDatabaseList = useCallback(async () => {
    try {
      setIsLoading(true);

      const dbList = await resilientFetch(
        () => cachedFetch('database-list', () => getDatabaseList()),
        { timeout: 30000, priority: 'high' }
      );

      setDatabases(dbList);
      setIsConnected(true);
    } catch (error) {
      console.error('[DatabaseSelection] Error loading database list:', error);
      setIsConnected(false);
      onError('Connection error. Please check your Supabase configuration.');
    } finally {
      setIsLoading(false);
    }
  }, [resilientFetch, cachedFetch, onError]);

  const loadStorageInfo = useCallback(async () => {
    try {
      const info = await resilientFetch(
        () => cachedFetch('storage-info', () => getDatabaseStorageInfo()),
        { timeout: 15000 }
      );
      setStorageInfo(info);
    } catch (error) {
      console.error('[DatabaseSelection] Error loading storage info:', error);
    }
  }, [resilientFetch, cachedFetch]);

  const handleLoadDatabase = useCallback(async (databaseName: string) => {
    setIsLoading(true);
    console.log(`[DatabaseSelection] Loading database: ${databaseName}`);

    try {
      const data = await resilientFetch(
        () => cachedFetch(
          `database-${databaseName}`,
          () => loadDatabase(databaseName),
          { backgroundRefresh: true }
        ),
        { timeout: 60000, priority: 'high' }
      );

      if (data && data.length > 0) {
        console.log(`[DatabaseSelection] Database loaded successfully: ${data.length} records`);
        onDatabaseSelected(data, databaseName);
      } else {
        onError(`Unable to load database "${databaseName}"`);
      }
    } catch (error) {
      console.error('[DatabaseSelection] Error loading database:', error);
      onError(`Error loading database: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [resilientFetch, cachedFetch, onDatabaseSelected, onError]);

  const handleNewFileUpload = useCallback(async (data: StockData[]) => {
    if (!newDatabaseName.trim()) {
      onError('Please enter a database name');
      return;
    }

    setIsLoading(true);
    console.log(`[DatabaseSelection] Creating new database: ${newDatabaseName}`);

    try {
      const { saveDatabase } = await import('../utils/optimizedDatabaseManager');

      const success = await resilientFetch(
        () => saveDatabase(newDatabaseName.trim(), data, newDatabaseDescription.trim()),
        { timeout: 120000, priority: 'high' }
      );

      if (success) {
        invalidateCache('database-list');
        invalidateCache('storage-info');

        console.log(`[DatabaseSelection] Database created successfully`);
        onDatabaseSelected(data, newDatabaseName.trim());
        navigate('/dashboard');
      } else {
        onError('Error saving database');
      }
    } catch (error) {
      console.error('[DatabaseSelection] Error creating database:', error);
      onError(error instanceof Error ? error.message : 'Error creating database');
    } finally {
      setIsLoading(false);
    }
  }, [newDatabaseName, newDatabaseDescription, resilientFetch, invalidateCache, onDatabaseSelected, onError, navigate]);

  const handleFileUploadError = (error: string) => {
    onError(error);
    setIsLoading(false);
  };

  const formatFileSize = (records: number) => {
    if (records < 1000) return `${records} record`;
    if (records < 1000000) return `${(records / 1000).toFixed(1)}K record`;
    return `${(records / 1000000).toFixed(1)}M record`;
  };
  
  // Track render time at the end of the component
  useEffect(() => {
    return () => {
      finishRenderTracking();
    };
  }, [finishRenderTracking]);

  // Mostra stato di connessione
  if (!isConnected) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="p-3 bg-red-400/10 rounded-xl">
              <WifiOff className="h-6 w-6 sm:h-8 sm:w-8 text-red-400" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-white">Connessione Database Non Disponibile</h1>
          </div>
          <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            Impossibile connettersi al database Supabase. Verifica la configurazione.
          </p>
          
          <div className="max-w-2xl mx-auto bg-gray-800/50 border border-gray-700 rounded-lg p-4 sm:p-6 text-left">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Configurazione Richiesta:</h3>
            <ol className="text-gray-300 space-y-2 text-xs sm:text-sm">
              <li>1. Crea un progetto su <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">supabase.com</a></li>
              <li>2. Vai su Settings → API e copia l'URL del progetto e la chiave anon</li>
              <li>3. Crea un file <code className="bg-gray-700 px-2 py-1 rounded">.env</code> nella root del progetto</li>
              <li>4. Aggiungi le variabili:
                <pre className="bg-gray-900 p-3 rounded mt-2 text-xs overflow-x-auto">
{`VITE_SUPABASE_URL=your_supabase_project_url
VITE_SUPABASE_ANON_KEY=your_supabase_anon_key`}
                </pre>
              </li>
              <li>5. Vai su SQL Editor in Supabase e esegui il contenuto del file <code className="bg-gray-700 px-2 py-1 rounded">supabase/migrations/20250710153540_blue_waterfall.sql</code></li>
              <li>6. Verifica che le tabelle <code className="bg-gray-700 px-2 py-1 rounded">databases</code> e <code className="bg-gray-700 px-2 py-1 rounded">stock_data</code> siano state create</li>
            </ol>
          </div>

          <button
            onClick={loadDatabaseList}
            className="mt-8 px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm sm:text-base"
            disabled={isLoading}
          >
            {isLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin mx-auto" />
            ) : (
              'Riprova Connessione'
            )}
          </button>
        </div>
      </div>
    );
  }

  if (showFileUpload) {
    return (
      <ErrorBoundary>
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-blue-400/10 rounded-xl">
                <Plus className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold text-white">Crea Nuovo Database</h1>
            </div>
            <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto">
              Carica un nuovo file Excel per creare un database nella piattaforma
            </p>
          </div>

          {/* Indicatore Connessione */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="bg-green-400/10 border border-green-400/30 rounded-lg p-3 sm:p-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Wifi className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 flex-shrink-0" />
                <div>
                  <h4 className="text-sm sm:text-base font-medium text-green-400">Connesso a Supabase</h4>
                  <p className="text-xs sm:text-sm text-green-300/80 mt-1">
                    Database cloud attivo - nessun limite di spazio di archiviazione
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Nuovo Database */}
          <div className="max-w-2xl mx-auto mb-6 sm:mb-8">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Informazioni Database</h3>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="dbName" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Nome Database *
                  </label>
                  <input
                    type="text"
                    id="dbName"
                    value={newDatabaseName}
                    onChange={(e) => setNewDatabaseName(e.target.value)}
                    placeholder="es. Q4_2024_Gaps"
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="dbDescription" className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Descrizione (opzionale)
                  </label>
                  <textarea
                    id="dbDescription"
                    value={newDatabaseDescription}
                    onChange={(e) => setNewDatabaseDescription(e.target.value)}
                    placeholder="Descrizione del contenuto del database..."
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <FileUpload 
            onDataUpload={handleNewFileUpload}
            onError={handleFileUploadError}
            isLoading={isLoading}
          />

          {/* Pulsante Indietro */}
          <div className="text-center mt-6 sm:mt-8">
            <button
              onClick={() => setShowFileUpload(false)}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm sm:text-base"
              disabled={isLoading}
            >
              ← Annulla
            </button>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
            <div className="p-2 sm:p-3 bg-blue-400/10 rounded-xl">
              <Database className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-white">Gestione Database</h1>
          </div>
          <p className="text-base sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Seleziona un database esistente o carica un nuovo file Excel per iniziare l'analisi
          </p>
        </div>

        {/* Indicatore Connessione */}
        <div className="max-w-4xl mx-auto mb-6 sm:mb-8">
          <div className="bg-green-400/10 border border-green-400/30 rounded-lg p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Wifi className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 flex-shrink-0" />
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-green-400">Database Cloud Attivo</h3>
                <p className="text-xs sm:text-sm text-green-300/80 mt-2">
                  Connesso a Supabase - archiviazione illimitata e sicura nel cloud. 
                  I tuoi dati sono protetti e accessibili da qualsiasi dispositivo.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Opzioni Principali */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto mb-8 sm:mb-12">
          {/* Carica Nuovo Database */}
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6 sm:p-8 hover:bg-gray-800/70 transition-all duration-200">
            <div className="text-center">
              <div className="p-3 sm:p-4 bg-blue-400/10 rounded-xl inline-block mb-3 sm:mb-4">
                <Upload className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Carica Nuovo File</h3>
              <p className="text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6">
                Crea un nuovo database caricando un file Excel con i dati dei gap
              </p>
              <button
                onClick={() => setShowFileUpload(true)}
                className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors font-medium text-sm sm:text-base"
                disabled={isLoading}
              >
                Carica File Excel
              </button>
            </div>
          </div>

          {/* Database Esistenti */}
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6 sm:p-8">
            <div className="text-center mb-4 sm:mb-6">
              <div className="p-3 sm:p-4 bg-green-400/10 rounded-xl inline-block mb-3 sm:mb-4">
                <HardDrive className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-2 sm:mb-3">Database Salvati</h3>
              <p className="text-xs sm:text-sm text-gray-400">
                {databases.length} database disponibili
              </p>
            </div>

            {databases.length === 0 ? (
              <div className="text-center py-6 sm:py-8">
                <p className="text-gray-500 mb-3 sm:mb-4">Nessun database salvato</p>
                <p className="text-xs sm:text-sm text-gray-600">Carica il tuo primo file Excel per iniziare</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-48 sm:max-h-64 overflow-y-auto">
                {databases.map((db) => (
                  <div
                    key={db.id}
                    className="bg-gray-900/30 border border-gray-600 rounded-lg p-3 sm:p-4 hover:bg-gray-900/50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1">
                        <h4 className="text-sm sm:text-base text-white font-medium">{db.name}</h4>
                        {db.description && (
                          <p className="text-xs text-gray-400 mt-1">{db.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                          <div className="flex items-center space-x-1">
                            <BarChart3 className="h-3 w-3" />
                            <span>{formatFileSize(db.recordCount)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-3 w-3" />
                            <span>{format(db.lastModified, 'dd/MM/yyyy', { locale: it })}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleLoadDatabase(db.name)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                        ) : (
                          'Carica'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Statistiche Database */}
        {databases.length > 0 && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Statistiche Database</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-400">{databases.length}</p>
                  <p className="text-xs sm:text-sm text-gray-400">Database Totali</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-400">
                    {databases.reduce((sum, db) => sum + db.recordCount, 0).toLocaleString()}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400">Record Totali</p>
                </div>
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-purple-400">
                    ∞
                  </p>
                  <p className="text-xs sm:text-sm text-gray-400">Spazio Disponibile</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};